/* eslint-disable @typescript-eslint/no-explicit-any */
import { createContext, useContext } from 'react'
import {
  AuthEmailType,
  IIpInfo,
  IQuery,
  IUser,
  Maybe
} from '../../types/graphql'

/**
 * Why the auth modal was opened — stamped onto `auth_modal_open` /
 * `auth_modal_abandoned` so the login gate's drop-off is attributable to the
 * funnel that hit it (e.g. "abandoned the auth wall during checkout" vs a
 * casual nav login). `other` is the default when a caller doesn't specify one.
 */
export type AuthModalTrigger =
  | 'checkout'
  | 'chat_host'
  | 'report'
  | 'nav'
  | 'other'

export type IAuthContext = {
  user: any | null | undefined
  currentUser: Maybe<IUser>
  ipInfo: Maybe<IIpInfo>
  isAuthenticated: boolean
  loading: boolean
  loadingCurrentUser: boolean
  toggleLoginModal: (open?: boolean, trigger?: AuthModalTrigger) => void
  /**
   * Whether the global login modal is open. Lets a caller that opened it for a follow-up action
   * (e.g. the calculator claim) forget that intent when the visitor dismisses it unauthenticated.
   */
  isLoginModalOpen: boolean
  logout: () => Promise<void>
  refetchCurrentUser: () => Promise<void>
  updateCurrentUser: (
    updateData: (
      data: Pick<IQuery, 'currentUser'>
    ) => Pick<IQuery, 'currentUser'>
  ) => void
  sendAuthEmail: (
    email: string,
    emailType: AuthEmailType,
    redirectUrl?: string
  ) => Promise<any>
  loadingSendAuthEmail: boolean
  referrer: string | undefined
  setReferrer: (referrer: string) => void
  /**
   * "The cookie banner is not in the way" — NOT "the user made a consent
   * choice". True when the banner was never going to show (blocked landing
   * path, consent cookie already present, dismissed earlier this session),
   * while it is showing on desktop (bottom-left card, no collision with the
   * top-right One Tap prompt), and once a mobile visitor accepts / rejects /
   * saves / dismisses. Consent gates nothing before a choice (gtag consent
   * default is all-granted); the only consumer is GooglePopup's One Tap gate.
   * Redefined 2026-09-14 — before, every blocked landing path and every
   * ignored mobile banner left One Tap disarmed for the whole SPA session.
   */
  cookiesDealt: boolean
}

export const AuthContext = createContext<IAuthContext>({
  user: null,
  currentUser: null,
  ipInfo: null,
  isAuthenticated: false,
  loading: false,
  loadingCurrentUser: false,
  toggleLoginModal: () => {},
  isLoginModalOpen: false,
  logout: async () => {},
  refetchCurrentUser: async () => {},
  updateCurrentUser: () => {},
  sendAuthEmail: async () => {},
  loadingSendAuthEmail: false,
  referrer: undefined,
  setReferrer: () => {},
  cookiesDealt: false
})

export const useAuthProvider = (): IAuthContext => useContext(AuthContext)
